import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { Container, Nav, NavItem, NavLink, Badge } from "reactstrap";
import classnames from "classnames";
import { scrollWindow } from "../../../../helpers/common";
import SmallLoader from "../../../../helpers/Common/SmallLoader";
import Nojob from "./Nojob";
import Section from "./Section";
import Selected from "./Selected";
import SingleJobApplication from "./SingleJobApplication";

// 🚨 IMPORT NOTE: Adjust this path to match where your API service file is located
import { getJobApplications } from "../../../../api/jobApplications";

const VALID_FILTERS = ["ALL", "PENDING", "SHORTLISTED", "REJECTED"];

const ManageJobApplications = () => {
  // Local states
  const [appliedJobs, setAppliedJobs] = useState({ data: [] });
  const [apiLoading, setApiLoading] = useState(false);

  // ✅ URL se status padho (?status=shortlisted)
  const [searchParams] = useSearchParams();
  const statusFromUrl = searchParams.get("status")?.toUpperCase();

  const [activeFilter, setActiveFilter] = useState(
    VALID_FILTERS.includes(statusFromUrl) ? statusFromUrl : "ALL"
  );

  // ✅ URL change hone par tab bhi change ho
  useEffect(() => {
    if (VALID_FILTERS.includes(statusFromUrl)) {
      setActiveFilter(statusFromUrl);
    }
  }, [statusFromUrl]);

  // Grab the logged in profile to filter applications by candidate
  const customer = useSelector((state) => state.customer?.profile);

  useEffect(() => {
    scrollWindow();

    if (customer?.id) {



      setApiLoading(true);

      getJobApplications(`&candidate_id=${customer.id}`)

      .then((res) => {
          const rawData = res?.data?.data || res?.data || [];
          const structuredData = Array.isArray(rawData)
            ? { data: rawData }


            : rawData;
          setAppliedJobs(structuredData);
        })
        .catch((err) => {
          console.error("❌ Candidate Panel API Error Details:", {
            errorMessage: err?.message,
            errorResponse: err?.response?.data,
            errorStatus: err?.response?.status,

            fullErrorObject: err,
          });
        })
        .finally(() => {


          setApiLoading(false);
        });
    } else {
      console.warn("⚠️ Candidate API call skipped: No customer profile id found in state.");
    }
  }, [customer?.id]);

  const loading = apiLoading;
  const rawList = Array.isArray(appliedJobs?.data) ? appliedJobs.data : [];

  // Count metrics for tab badges
  const counts = {
    ALL: rawList.length,
    PENDING: rawList.filter((item) => item?.status?.toLowerCase() === "pending").length,
    SHORTLISTED: rawList.filter((item) => item?.status?.toLowerCase() === "shortlisted").length,
    REJECTED: rawList.filter((item) => item?.status?.toLowerCase() === "rejected").length,
  };

  // Filter list according to the active tab selection
  const filteredJobs = rawList.filter((item) => {
    if (activeFilter === "ALL") return true;
    if (activeFilter === "PENDING") return item?.status?.toLowerCase() === "pending";
    if (activeFilter === "SHORTLISTED") return item?.status?.toLowerCase() === "shortlisted";
    if (activeFilter === "REJECTED") return item?.status?.toLowerCase() === "rejected";
    return true;
  });

  return (
    <React.Fragment>
      <Helmet>
        <title>Job Application | Webvi Page </title>
      </Helmet>
      <Section />




      {loading ? (

<SmallLoader />
      ) : (

<section className="section">
  <Container>
    {/* Status Filter Tabs - Centered & Neutral Styling */}
    <div className="bg-white border rounded-3 p-3 mb-4 shadow-sm">
      <Selected />
      <Nav pill className="gap-2 justify-content-center flex-wrap">
        <NavItem>
          <NavLink
            style={{ cursor: "pointer" }}
            className={classnames("fw-medium rounded-3 px-3 py-2", {
              active: activeFilter === "ALL",
              "bg-primary text-white": activeFilter === "ALL",
              "text-secondary bg-light": activeFilter !== "ALL",
            })}
            onClick={() => setActiveFilter("ALL")}
          >
            All Applications{" "}
            <Badge
              pill
              color={activeFilter === "ALL" ? "light" : "secondary"}
              className={activeFilter === "ALL" ? "text-primary ms-1" : "ms-1"}
            >
              {counts.ALL}
            </Badge>
          </NavLink>
        </NavItem>

        <NavItem>
          <NavLink
            style={{ cursor: "pointer" }}
            className={classnames("fw-medium rounded-3 px-3 py-2", {
              active: activeFilter === "PENDING",
              "bg-primary text-white": activeFilter === "PENDING",
              "text-secondary bg-light": activeFilter !== "PENDING",
            })}
            onClick={() => setActiveFilter("PENDING")}
          >
            Pending{" "}
            <Badge
              pill
              color={activeFilter === "PENDING" ? "light" : "secondary"}
              className={activeFilter === "PENDING" ? "text-primary ms-1" : "ms-1"}
            >
              {counts.PENDING}
            </Badge>
          </NavLink>
        </NavItem>

        <NavItem>
          <NavLink
            style={{ cursor: "pointer" }}
            className={classnames("fw-medium rounded-3 px-3 py-2", {
              active: activeFilter === "SHORTLISTED",
              "bg-primary text-white": activeFilter === "SHORTLISTED",
              "text-secondary bg-light": activeFilter !== "SHORTLISTED",
            })}
            onClick={() => setActiveFilter("SHORTLISTED")}
          >
            Shortlisted{" "}
            <Badge
              pill
              color={activeFilter === "SHORTLISTED" ? "light" : "secondary"}
              className={activeFilter === "SHORTLISTED" ? "text-primary ms-1" : "ms-1"}
            >
              {counts.SHORTLISTED}
            </Badge>
          </NavLink>
        </NavItem>

        <NavItem>
          <NavLink
            style={{ cursor: "pointer" }}
            className={classnames("fw-medium rounded-3 px-3 py-2", {
              active: activeFilter === "REJECTED",
              "bg-primary text-white": activeFilter === "REJECTED",
              "text-secondary bg-light": activeFilter !== "REJECTED",
            })}
            onClick={() => setActiveFilter("REJECTED")}
          >
            Rejected{" "}
            <Badge
              pill
              color={activeFilter === "REJECTED" ? "light" : "secondary"}
              className={activeFilter === "REJECTED" ? "text-primary ms-1" : "ms-1"}
            >
              {counts.REJECTED}
            </Badge>
          </NavLink>
        </NavItem>
      </Nav>
    </div>

    {/* List View Render */}
    {filteredJobs.length > 0 ? (
      <>
        {filteredJobs.map((details) => (
          <SingleJobApplication
            details={details}
            key={details.id || details.jobId}
          />
        ))}
      </>
    ) : (
      <Nojob
        message={`No ${
          activeFilter !== "ALL" ? activeFilter.toLowerCase() : ""
        } job application found.`}
      />
    )}
  </Container>
</section>
      )}
    </React.Fragment>
  );
};

export default ManageJobApplications;